import { useCallback, useEffect, useState } from 'react'
import { FileText, Loader2, Receipt } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { SaleDocumentPrintHosts } from '@/components/sales/SaleDocumentPrintHosts'
import { useSaleDocumentPrint } from '@/hooks/useSaleDocumentPrint'
import {
  fetchCustomer360,
  formatCustomer360Amount,
  formatCustomer360Date,
} from '@/lib/api/customer360'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {number | null} props.customerId
 * @param {'masters' | 'pos'} [props.variant='masters']
 * @param {number} [props.visitLimit=25]
 */
export function CustomerVisitHistoryPanel({
  customerId,
  variant = 'masters',
  visitLimit = 25,
}) {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const {
    reprintReceipt,
    printTaxInvoiceForSale,
    busySaleId,
    busyAction,
    error: printError,
    setError: setPrintError,
    receipt,
    taxInvoice,
    receiptRootRef,
    taxInvoiceRootRef,
  } = useSaleDocumentPrint()

  const isPos = variant === 'pos'
  const textMuted = isPos ? 'text-slate-500' : 'text-muted-foreground'
  const textBody = isPos ? 'text-slate-800' : 'text-foreground'
  const cardClass = isPos
    ? 'rounded-md border border-[#E5E7EB] bg-white'
    : 'rounded-xl border border-border/70 bg-card'

  const loadProfile = useCallback(async () => {
    if (!customerId) {
      setProfile(null)
      setLoadError('')
      return
    }

    setLoading(true)
    setLoadError('')
    setPrintError('')

    try {
      const data = await fetchCustomer360(customerId, {
        limit: visitLimit,
        source: isPos ? 'pos' : 'masters',
      })
      setProfile(data)
    } catch (err) {
      setProfile(null)
      setLoadError(err?.response?.data?.message ?? 'Unable to load visit history.')
    } finally {
      setLoading(false)
    }
  }, [customerId, isPos, setPrintError, visitLimit])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  if (!customerId) {
    return (
      <p className={cn('text-sm', textMuted)}>
        Save the customer first to view visit history.
      </p>
    )
  }

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 py-8 text-sm', textMuted)}>
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading visit history…
      </div>
    )
  }

  if (loadError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {loadError}
      </p>
    )
  }

  if (!profile) {
    return (
      <p className={cn('text-sm', textMuted)}>
        No visit history available.
      </p>
    )
  }

  const { summary, favourite_services: favourites, visits } = profile

  return (
    <div className="space-y-5">
      <SaleDocumentPrintHosts
        receipt={receipt}
        taxInvoice={taxInvoice}
        receiptRootRef={receiptRootRef}
        taxInvoiceRootRef={taxInvoiceRootRef}
      />

      <div className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-4', isPos ? 'text-[11px]' : 'text-sm')}>
        <SummaryStat
          label="Visits"
          value={String(summary.visit_count)}
          className={cardClass}
          isPos={isPos}
        />
        <SummaryStat
          label="Lifetime spend"
          value={formatCustomer360Amount(summary.lifetime_spend)}
          className={cardClass}
          isPos={isPos}
        />
        <SummaryStat
          label="Average ticket"
          value={formatCustomer360Amount(summary.average_ticket)}
          className={cardClass}
          isPos={isPos}
        />
        <SummaryStat
          label="Last visit"
          value={formatCustomer360Date(summary.last_visit_at)}
          className={cardClass}
          isPos={isPos}
        />
      </div>

      {summary.loyalty_enabled ? (
        <div className={cn(cardClass, 'px-4 py-3', isPos ? 'text-[11px]' : 'text-sm')}>
          <span className={cn('font-medium', textBody)}>Loyalty balance: </span>
          <span>{summary.loyalty_points_balance} pts</span>
        </div>
      ) : null}

      {favourites.length > 0 ? (
        <section>
          <h3 className={cn('mb-2 font-semibold', isPos ? 'text-[10px] uppercase tracking-wide text-slate-500' : 'text-sm')}>
            Favourite services
          </h3>
          <div className="flex flex-wrap gap-2">
            {favourites.map((item) => (
              <span
                key={item.service_id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-1',
                  isPos ? 'bg-slate-100 text-[11px] text-slate-700' : 'bg-muted/70 text-xs',
                )}
              >
                {item.service_name}
                <span className={textMuted}>×{item.visit_count}</span>
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <h3 className={cn('mb-3 font-semibold', isPos ? 'text-[10px] uppercase tracking-wide text-slate-500' : 'text-sm')}>
          Visit history
        </h3>

        {visits.length === 0 ? (
          <p className={cn('text-sm', textMuted)}>No completed visits yet.</p>
        ) : (
          <div className="space-y-3">
            {visits.map((visit) => {
              const isRefunded = visit.status === 'refunded'
              const isBusy = busySaleId === visit.sale_id

              return (
                <article key={visit.sale_id} className={cn(cardClass, 'p-4')}>
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className={cn('font-semibold', textBody, isPos && 'text-xs')}>
                        #{visit.sale_number}
                        {isRefunded ? (
                          <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium uppercase text-red-700">
                            Refunded
                          </span>
                        ) : null}
                      </p>
                      <p className={cn('mt-0.5', textMuted, isPos ? 'text-[11px]' : 'text-xs')}>
                        {formatCustomer360Date(visit.completed_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={cn('font-mono font-bold', textBody, isPos ? 'text-sm' : 'text-base')}>
                        {formatCustomer360Amount(visit.total)}
                      </p>
                      {Number(visit.discount_amount) > 0 ? (
                        <p className={cn(textMuted, isPos ? 'text-[10px]' : 'text-xs')}>
                          Disc {formatCustomer360Amount(visit.discount_amount)}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {visit.services.length > 0 ? (
                    <div className={cn('mb-3 overflow-x-auto border-t border-border/50 pt-3', isPos && 'border-[#E5E7EB]')}>
                      <table className={cn('w-full text-left', isPos ? 'text-[11px]' : 'text-sm')}>
                        <thead>
                          <tr className={cn('border-b', isPos ? 'border-[#E5E7EB]' : 'border-border/50')}>
                            <th className={cn('pb-1.5 pr-3 font-medium', textMuted)}>Service</th>
                            <th className={cn('pb-1.5 pr-3 font-medium', textMuted)}>Stylist</th>
                            <th className={cn('pb-1.5 pr-3 font-medium', textMuted)}>Chair</th>
                            <th className={cn('pb-1.5 text-right font-medium', textMuted)}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visit.services.map((line) => (
                            <tr
                              key={`${visit.sale_id}-${line.service_id}-${line.service_name}`}
                              className={cn('border-b last:border-b-0', isPos ? 'border-[#E5E7EB]/70' : 'border-border/30')}
                            >
                              <td className={cn('py-1.5 pr-3', textBody)}>{line.service_name}</td>
                              <td className={cn('py-1.5 pr-3', textMuted)}>
                                {line.stylist_name || '—'}
                              </td>
                              <td className={cn('py-1.5 pr-3', textMuted)}>
                                {line.chair_name || '—'}
                              </td>
                              <td className="py-1.5 text-right font-mono">
                                {formatCustomer360Amount(line.line_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className={cn('mb-3 text-xs', textMuted)}>No service lines on this ticket.</p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => reprintReceipt(visit.sale_id)}
                      className={isPos ? 'h-8 text-[11px]' : undefined}
                    >
                      {isBusy && busyAction === 'receipt' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Receipt className="h-3.5 w-3.5" />
                      )}
                      Reprint receipt
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isBusy || isRefunded}
                      onClick={() => printTaxInvoiceForSale(visit.sale_id)}
                      className={isPos ? 'h-8 text-[11px]' : undefined}
                      title={isRefunded ? 'Tax invoice not available for refunded tickets' : undefined}
                    >
                      {isBusy && busyAction === 'invoice' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <FileText className="h-3.5 w-3.5" />
                      )}
                      Tax invoice
                    </Button>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>

      {printError ? (
        <p className="text-sm text-destructive" role="alert">
          {printError}
        </p>
      ) : null}
    </div>
  )
}

/**
 * @param {{ label: string, value: string, className: string, isPos: boolean }} props
 */
function SummaryStat({ label, value, className, isPos }) {
  return (
    <div className={cn(className, 'px-3 py-2.5')}>
      <p className={cn(isPos ? 'text-[10px] uppercase tracking-wide text-slate-500' : 'text-xs text-muted-foreground')}>
        {label}
      </p>
      <p className={cn('mt-0.5 font-semibold', isPos ? 'text-xs text-slate-900' : 'text-sm')}>
        {value}
      </p>
    </div>
  )
}
